/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { ActionList, InfoCardKernel, InfoCardKernelType } from 'client-code/actions';

import { TIVO_SERVICES } from '../../../config';
import { IInfoCardEvents, IInfoCardProperty, InfoCardManager } from '../../../core/infocard';
import { ImageAssetsService } from '../../../core/infrastructure';
import { ServicesClientImpl } from '../../../core/interactors';
import { SharedPrefConstants, SharedPreferences } from '../../../core/storage';
import { RootState, useTivoDispatch, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import {
    TContentPreview,
    TOfferPreview,
    TUnifiedCollectionPreview,
    TVodOfferPreview,
} from '../../../core/types';
import { InfocardActions, MetadataOperations, PreviewComponent } from '../../../core/usecases';
import {
    onVisibilityHideInfoCardOperations,
    onVisibilityShowInfoCardOperations,
} from '../../../store/infocard/infocard.reducers';
import EpisodeInfoCard from './EpisodeInfoCard';
import { InfoCardProps } from './InfoCardProps';
import { InfoCardUserAction } from './InfoCardUserAction';
import MovieInfoCard from './MovieInfoCard';
import NonEpisodicInfoCard from './NonEpisodicInfoCard';
import SeriesInfoCard from './SeriesInfoCard';
import TeamAndLeagueInfoCard from './TeamAndLeagueInfoCard';

import './InfoCard.scss';

const InfoCard: FunctionComponent = () => {
    const [contentPreviewInfo, setContentPreviewInfo] = useState<TContentPreview>();
    const [offerPreviewInfo, setOfferPreviewInfo] = useState<TOfferPreview>();
    const [infoCardKernelType, setInfoCardKernelType] = useState<InfoCardKernelType>();
    const [recordingPreviewInfo, setRecordingPreviewInfo] = useState<TOfferPreview>();
    // eslint-disable-next-line no-unused-vars
    const [unicPreviewInfo, setUnicPreviewInfo] = useState<TUnifiedCollectionPreview>();
    const [vodOfferPreviewInfo, setVodOfferPreviewInfo] = useState<TVodOfferPreview>();
    const [infocardProperties, setInfocardProperties] = useState<IInfoCardProperty>();
    // eslint-disable-next-line no-unused-vars
    const [actionsOptions, setActionsOptions] = useState<InfoCardUserAction[]>([]);
    const [isActionMode, setIsActionMode] = useState<boolean>(false);
    const dispatch = useTivoDispatch();

    const oInfoCardManagerRef = useRef<InfoCardManager | null>(
        InfoCardManager.getInstance() as InfoCardManager,
    );

    const highlight = useTivoSelector((state: RootState) => state.infocard.highlight);
    const [showInfoCard, setShowInfoCard] = useState<boolean>(false);
    const getInfoCard = (metadata: InfoCardProps['metadata']) => {
        return (
            <React.Fragment>
                <MovieInfoCard
                    metadata={metadata}
                    properties={infocardProperties}
                    isActionMode={isActionMode}
                    highlight={highlight}
                    userActions={actionsOptions}
                />
                <EpisodeInfoCard
                    metadata={metadata}
                    properties={infocardProperties}
                    isActionMode={isActionMode}
                    highlight={highlight}
                    userActions={actionsOptions}
                />
                <NonEpisodicInfoCard
                    metadata={metadata}
                    properties={infocardProperties}
                    isActionMode={isActionMode}
                    highlight={highlight}
                    userActions={actionsOptions}
                />
                <TeamAndLeagueInfoCard
                    metadata={metadata}
                    isActionMode={isActionMode}
                    highlight={highlight}
                    userActions={actionsOptions}
                />
                <SeriesInfoCard
                    metadata={metadata}
                    isActionMode={isActionMode}
                    highlight={highlight}
                    userActions={actionsOptions}
                />
            </React.Fragment>
        );
    };

    const ContentInfoCardKernel: FunctionComponent = () => {
        if (infoCardKernelType !== InfoCardKernelType.ContentInfoCardKernel || !contentPreviewInfo)
            return null;

        return getInfoCard(contentPreviewInfo);
    };

    const OfferInfoCardKernel: FunctionComponent = () => {
        const shouldDrawPreview =
            infoCardKernelType === InfoCardKernelType.OfferInfoCardKernel && offerPreviewInfo;
        if (!shouldDrawPreview) return null;
        const { contentPreview } = shouldDrawPreview.offerPreview;
        if (!contentPreview) return null;
        const service = new ImageAssetsService({
            imageHostUrl: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost],
        });

        service.getMoviePosterImageUrl(contentPreview.contentId || '');

        return getInfoCard(offerPreviewInfo);
    };

    const RecordingInfoCardKernel: FunctionComponent = () => {
        const shouldDrawPreview =
            infoCardKernelType === InfoCardKernelType.RecordingInfoCardKernel &&
            recordingPreviewInfo;
        if (!shouldDrawPreview) return null;

        const { contentPreview } = recordingPreviewInfo.offerPreview;
        if (!contentPreview) return null;

        return getInfoCard(recordingPreviewInfo);
    };

    const UnifiedCollectionInfoCardKernel: FunctionComponent = () => {
        const shouldDrawPreview =
            infoCardKernelType === InfoCardKernelType.UnifiedCollectionInfoCardKernel &&
            unicPreviewInfo;
        if (!shouldDrawPreview) return null;

        return getInfoCard(unicPreviewInfo);
    };

    const VodOfferPreviewInfoCardKernel: FunctionComponent = () => {
        const shouldDrawPreview =
            infoCardKernelType === InfoCardKernelType.VodOfferInfoCardKernel && vodOfferPreviewInfo;
        if (!shouldDrawPreview) return null;

        const { vodOfferPreview } = vodOfferPreviewInfo;
        const { contentPreview } = vodOfferPreview;

        // TODO : We need to implement VOD offer info card
        return (
            <React.Fragment>
                <div className="info-card-container">
                    <div className="info-card">
                        <h2 style={{ color: 'white' }}>
                            Vod Offer Id: {contentPreview?.title || 'my data'}
                        </h2>
                    </div>
                </div>
            </React.Fragment>
        );
    };

    const narrateInfoCard = (): JSX.Element | null => {
        if (!showInfoCard) return null;
        return (
            <React.Fragment>
                <ContentInfoCardKernel />
                <RecordingInfoCardKernel />
                <OfferInfoCardKernel />
                <UnifiedCollectionInfoCardKernel />
                <VodOfferPreviewInfoCardKernel />
            </React.Fragment>
        );
    };

    const handleOnInfoCardKernelChanged = (infoCardKernel: InfoCardKernel | null) => {
        setActionsOptions([]);
        if (infoCardKernel) {
            processInfoCardKernel(infoCardKernel);
            getActionsOptions(infoCardKernel);
        } else setInfoCardKernelType(undefined);
    };

    const handleOnInfoCardPropertiesChanged = (properties: IInfoCardProperty) => {
        setInfocardProperties(properties);
        setIsActionMode(properties.actionButtonVisibility);
    };

    const handleOnInfoCardVisibilityChanged = (isVisible: boolean) => {
        setShowInfoCard(isVisible);
        if (isVisible) {
            dispatch(onVisibilityShowInfoCardOperations());
        } else {
            dispatch(onVisibilityHideInfoCardOperations());
        }
    };

    const getActionsOptions = async (aInfoCardKernel: InfoCardKernel) => {
        const useCases = new InfocardActions(aInfoCardKernel);
        const metadataOperations = new MetadataOperations();

        let actions: ActionList = { actions: [] };
        let infoCardKernelMetadata: InfoCardUserAction[] = [];

        try {
            switch (aInfoCardKernel.infoCardKernelType) {
                case InfoCardKernelType.ContentInfoCardKernel:
                    actions = await useCases.getContentActionsInfoCard();
                    break;

                case InfoCardKernelType.OfferInfoCardKernel:
                    actions = await useCases.getOfferActionsInfoCard();
                    break;

                case InfoCardKernelType.RecordingInfoCardKernel:
                    actions = await useCases.getRecordingActionsInfoCard();
                    break;

                case InfoCardKernelType.UnifiedCollectionInfoCardKernel:
                    actions = await useCases.getUnifiedCollectionActionsInfoCard();
                    break;

                case InfoCardKernelType.VodOfferInfoCardKernel:
                    actions = await useCases.getVodActionsInfoCard();
                    break;

                default:
            }
            infoCardKernelMetadata =
                metadataOperations.getMetaDataForInfoCardActionsOptions(actions);
            const sharedPrefMetadata = new SharedPreferences(
                SharedPrefConstants.INFO_CARD_PREF_KEY,
            ).edit();
            sharedPrefMetadata.putJson(
                SharedPrefConstants.INFO_CARD_OPTIONS,
                infoCardKernelMetadata as unknown as JSON,
            );
            sharedPrefMetadata.apply();
        } catch (error) {
            Diagnostics.error('InfoCard', 'Get Actions Options', error);
            infoCardKernelMetadata = [];
        }
        const actionCount = infoCardKernelMetadata.length;
        oInfoCardManagerRef.current?.setProperties({
            actionButtonCount: actionCount,
        });
        setActionsOptions(infoCardKernelMetadata);
    };

    const processInfoCardKernel = async (aInfoCardKernel: InfoCardKernel) => {
        setInfoCardKernelType(aInfoCardKernel.infoCardKernelType);
        const useCases = new PreviewComponent(aInfoCardKernel);

        switch (aInfoCardKernel.infoCardKernelType) {
            case InfoCardKernelType.ContentInfoCardKernel:
                setContentPreviewInfo(await useCases.getContentPreviewInfo());
                break;
            case InfoCardKernelType.InfoCardKernel:
                // Handle info card mode
                break;
            case InfoCardKernelType.OfferInfoCardKernel:
                setOfferPreviewInfo(await useCases.getOfferPreviewInfo());
                break;
            case InfoCardKernelType.RecordingInfoCardKernel:
                setRecordingPreviewInfo(await useCases.getRecordingPreviewInfo());
                break;
            case InfoCardKernelType.UnifiedCollectionInfoCardKernel:
                setUnicPreviewInfo(await useCases.getUnifiedCollectionPreviewInfo());
                break;
            case InfoCardKernelType.VodOfferInfoCardKernel:
                setVodOfferPreviewInfo(await useCases.getVodOfferPreviewV2Info());
                break;
            default:
                break;
        }
    };

    const subscribeInfoCardEvents = () => {
        if (oInfoCardManagerRef.current) {
            const oInfoCardEvents: IInfoCardEvents = oInfoCardManagerRef.current;
            oInfoCardEvents.onInfoCardKernelChanged(handleOnInfoCardKernelChanged);
            oInfoCardEvents.onInfoCardVisibilityChanged(handleOnInfoCardVisibilityChanged);
            oInfoCardEvents.onInfoCardPropertyChanged(handleOnInfoCardPropertiesChanged);
        }
    };

    useEffect(() => {
        if (oInfoCardManagerRef.current) {
            subscribeInfoCardEvents();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <React.Fragment>{narrateInfoCard()}</React.Fragment>;
};

export default InfoCard;
